import api, { API_ENDPOINTS, storeToken, storeRefreshToken, removeToken } from './api';

export const signInApi = async (data) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;

    let requestData = {};

    // Handle SSO login
    if (data.loginType === 'SSO' && data.idToken) {
        requestData = {
            loginType: 'SSO',
            idToken: data.idToken,
            fcmToken: data.fcmToken || ''
        };
    }
    // Handle email login
    else if (data.email && emailRegex.test(data.email)) {
        requestData = {
            email: data.email,
            password: data.password,
            loginType: 'EMAIL',
            fcmToken: data.fcmToken || ''
        };
    }
    // Handle phone login
    else if (data.phone && phoneRegex.test(data.phone)) {
        requestData = {
            phone: data.phone,
            countryCode: data.countryCode || '+1',
            password: data.password,
            loginType: 'PHONE',
            fcmToken: data.fcmToken || ''
        };
    }
    // Handle username login
    else if (data.username) {
        requestData = {
            username: data.username,
            password: data.password,
            loginType: 'USERNAME',
            fcmToken: data.fcmToken || ''
        };
    }

    return api.post(API_ENDPOINTS.SIGN_IN, requestData);
};

export const signUpApi = async (data) => {
    // Backend requires `signupMethod` ∈ EMAIL/PHONE. We only ship email for
    // now; phone signup would also need countryCode. Mirrors the mobile
    // register body (useSignUp): lowercased email/username + optional manual
    // referral code. Register creates a TEMP user and emails a 4-digit OTP —
    // the real account only exists after verify-email succeeds.
    const requestData = {
        email: (data.email || '').toLowerCase(),
        username: (data.username || '').toLowerCase(),
        password: data.password,
        signupMethod: 'EMAIL',
        fcmToken: data.fcmToken || '',
        ...(data.referralCode
            ? { referralCode: data.referralCode.trim(), referralCodeSource: 'manual' }
            : {})
    };

    return api.post(API_ENDPOINTS.SIGN_UP, requestData);
};

export const signInWithGoogleApi = async ({ idToken, fcmToken = '' }) => {
    const requestData = {
        loginType: 'SSO',
        idToken,
        fcmToken
    };
    return api.post(API_ENDPOINTS.SIGN_IN, requestData);
};

// Apple uses the SAME backend SSO endpoint as Google. The backend's verifyIdToken
// detects the provider from the token issuer, so we only pass the Apple identity
// token (id_token) as `idToken`. The login validator is strict — do NOT add extra
// fields (user/name/email/code/nonce would be rejected).
export const signInWithAppleApi = async ({ idToken, fcmToken = '' }) => {
    const requestData = {
        loginType: 'SSO',
        idToken,
        fcmToken
    };
    return api.post(API_ENDPOINTS.SIGN_IN, requestData);
};

// The verification routes authenticate with the SIGNUP token from register
// (a temp-user JWT), not a real session token — pass it explicitly so the
// request interceptor doesn't fall back to whatever session is stored.
const signupTokenHeaders = (signupToken) =>
    signupToken ? { headers: { accesstoken: `Bearer ${signupToken}` } } : undefined;

export const verifyEmailOtpApi = async (data, signupToken) => {
    // Joi expects `otp` as a number.
    const payload = { email: data.email, otp: Number(data.otp) };
    return api.post(API_ENDPOINTS.VERIFY_EMAIL_OTP, payload, signupTokenHeaders(signupToken));
};

export const resendEmailVerificationApi = async (signupToken) => {
    return api.post(API_ENDPOINTS.SEND_EMAIL_VERIFICATION, {}, signupTokenHeaders(signupToken));
};

// Final signup step (mirrors mobile SelectPreferences → POST /user): completes
// the account with name/gender/dob/bio/photo/preferences. Responds with fresh
// session tokens + the completed profile.
export const createProfileApi = async (data) => {
    return api.post(API_ENDPOINTS.CREATE_PROFILE, data);
};

// Uploads a profile photo to the shared asset bucket; response `data._id` is
// the `profilePictureId` for createProfileApi.
export const uploadAssetApi = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'asset');
    return api.post(API_ENDPOINTS.UPLOAD_FILE, formData);
};

export const getGuestCategoriesApi = async () => {
    return api.get(API_ENDPOINTS.CATEGORY_GUEST_LIST, { cacheBust: false });
};

export const enterMobileApi = async (data) => {
    return api.post(API_ENDPOINTS.ENTER_MOBILE, data);
};

export const verifyMobileOtpApi = async (data) => {
    return api.post(API_ENDPOINTS.VERIFY_MOBILE_OTP, data);
};

export const forgotPasswordApi = async (data) => {
    // Backend requires userType ∈ user/admin and email OR (phone + countryCode).
    const payload = { userType: data.userType || 'user', ...data };
    return api.post(API_ENDPOINTS.FORGOT_PASSWORD, payload);
};

export const verifyForgotOtpApi = async (data) => {
    return api.post(API_ENDPOINTS.VERIFY_FORGOT_OTP, data);
};

export const validateForgotPasswordApi = async (data) => {
    return api.post(API_ENDPOINTS.VALIDATE_FORGOT_PASSWORD, data);
};

export const checkUserNameApi = async (data) => {
    return api.post(API_ENDPOINTS.CHECK_USER_NAME, data);
};

export const checkEmailApi = async (data) => {
    return api.post(API_ENDPOINTS.CHECK_EMAIL, data);
};

export const userLogoutApi = async () => {
    return api.post(API_ENDPOINTS.LOGOUT);
};

export const handleSignIn = async (values, setIsLoading, setError, navigate) => {
    setIsLoading(true);
    setError('');

    try {
        let data = {};
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;

        const fcmToken = '';

        if (emailRegex.test(values.usernamelogin)) {
            data = {
                email: values.usernamelogin,
                password: values.password,
                loginType: 'EMAIL',
                fcmToken: fcmToken
            };
        } else if (phoneRegex.test(values.usernamelogin)) {
            data = {
                phone: values.usernamelogin,
                countryCode: '+1',
                password: values.password,
                loginType: 'PHONE',
                fcmToken: fcmToken
            };
        } else if (values.loginType === 'SSO' && values.idToken) {
            data = {
                loginType: 'SSO',
                idToken: values.idToken,
                fcmToken: fcmToken
            };
        } else {
            data = {
                username: values.usernamelogin,
                password: values.password,
                loginType: 'USERNAME',
                fcmToken: fcmToken
            };
        }

        const response = await signInApi(data);

        if (response?.data?.data?.token) {
            await storeToken(response.data.data.token);
        }
        if (response?.data?.data?.refreshToken) {
            await storeRefreshToken(response.data.data.refreshToken);
        }

        if (response?.data?.data?.profile?.isProfileCreated) {
            const user = response.data.data.profile;

            const userData = {
                id: user.id || '1',
                email: user.email || values.usernamelogin,
                name: user.name || user.username || 'User',
                avatar: user.avatar || "/images/pagent-logo-icon.png",
                token: response.data.data.token,
                isProfileCreated: true,
                ...user
            };

            localStorage.setItem("pagent_user", JSON.stringify(userData));

            navigate('/');
        } else {
            const userData = {
                id: response?.data?.data?.profile?.id || '1',
                email: values.usernamelogin,
                name: response?.data?.data?.profile?.name || response?.data?.data?.profile?.username || 'User',
                avatar: "/images/pagent-logo-icon.png",
                token: response.data.data.token,
                isProfileCreated: false,
                ...response?.data?.data?.profile
            };

            localStorage.setItem("pagent_user", JSON.stringify(userData));

            navigate('/register');
        }

        return { success: true };

    } catch (error) {
        console.error('Error signing in:', error);

        let errorMessage = 'An unexpected error occurred';

        if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.response?.status === 401) {
            errorMessage = 'Invalid credentials';
        } else if (error.response?.status === 404) {
            errorMessage = 'User not found';
        } else if (error.response?.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
        }

        setError(errorMessage);
        return { success: false, error: errorMessage };
    } finally {
        setIsLoading(false);
    }
};

export const handleGoogleSignIn = async ({ idToken }, setIsLoading, setError, navigate) => {
    setIsLoading(true);
    setError('');
    try {
        const response = await signInWithGoogleApi({ idToken });

        if (response?.data?.data?.token) {
            await storeToken(response.data.data.token);
        }
        if (response?.data?.data?.refreshToken) {
            await storeRefreshToken(response.data.data.refreshToken);
        }

        const profile = response?.data?.data?.profile || {};
        const userData = {
            id: profile.id || '1',
            email: profile.email || '',
            name: profile.name || profile.username || 'User',
            avatar: profile.avatar || "/images/pagent-logo-icon.png",
            token: response?.data?.data?.token,
            isProfileCreated: !!profile.isProfileCreated,
            ...profile,
        };
        localStorage.setItem("pagent_user", JSON.stringify(userData));

        if (userData.isProfileCreated) {
            navigate('/');
        } else {
            navigate('/register');
        }

        return { success: true };
    } catch (error) {
        console.error('Google sign-in failed:', error);
        const msg = error?.response?.data?.message || 'Google sign-in failed';
        setError(msg);
        return { success: false, error: msg };
    } finally {
        setIsLoading(false);
    }
};

export const handleAppleSignIn = async ({ idToken }, setIsLoading, setError, navigate) => {
    setIsLoading(true);
    setError('');
    try {
        const response = await signInWithAppleApi({ idToken });

        if (response?.data?.data?.token) {
            await storeToken(response.data.data.token);
        }
        if (response?.data?.data?.refreshToken) {
            await storeRefreshToken(response.data.data.refreshToken);
        }

        const profile = response?.data?.data?.profile || {};
        const userData = {
            id: profile.id || '1',
            email: profile.email || '',
            name: profile.name || profile.username || 'User',
            avatar: profile.avatar || "/images/pagent-logo-icon.png",
            token: response?.data?.data?.token,
            isProfileCreated: !!profile.isProfileCreated,
            ...profile,
        };
        localStorage.setItem("pagent_user", JSON.stringify(userData));

        if (userData.isProfileCreated) {
            navigate('/');
        } else {
            navigate('/register');
        }

        return { success: true };
    } catch (error) {
        console.error('Apple sign-in failed:', error);
        const msg = error?.response?.data?.message || 'Apple sign-in failed';
        setError(msg);
        return { success: false, error: msg };
    } finally {
        setIsLoading(false);
    }
};

export const handleLogout = async (navigate) => {
    try {
        await userLogoutApi();
    } catch (error) {
        console.error('Logout API error:', error);
    } finally {
        removeToken();
        localStorage.removeItem("pagent_user");
        navigate('/');
    }
}; 
